import { useState, useEffect } from 'react';
import TopNav from './TopNav';
import WordBinder from './WordBinder';
import ChatDrawer from './ChatDrawer';
import { useUiStore } from '../stores/uiStore';
import { useConfigStore } from '../stores/configStore';
import GameWorkspace from '../pages/GameWorkspace';
import SettingsPage from '../pages/SettingsPage';
import KnowledgePage from '../pages/KnowledgePage';
import LearningPage from '../pages/LearningPage';
import TestBenchPage from '../pages/TestBenchPage';
import PromptBenchPage from '../pages/PromptBenchPage';
import BakeoffPage from '../pages/BakeoffPage';
import RatingsPage from '../pages/RatingsPage';
import CopyPoolsPage from '../pages/CopyPoolsPage';
import WordBankPage from '../pages/WordBankPage';
import ComingSoon from '../pages/ComingSoon';
import ContentPage from '../pages/ContentPage';
import SchedulePage from '../pages/SchedulePage';
import ReportsPage from '../pages/ReportsPage';
import SocialPanelPage from '../pages/SocialPanelPage';
import './AppLayout.css';

function useWideViewport(breakpoint: number = 1024): boolean {
  const [isWide, setIsWide] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.innerWidth > breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint + 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isWide;
}

export default function AppLayout() {
  const { activeWorkspace, activePage, chatOpen } = useUiStore();
  const isWide = useWideViewport();
  const splitChat = chatOpen && isWide;

  useEffect(() => {
    useConfigStore.getState().fetchAutoGenerate();
  }, []);

  const renderMain = () => {
    if (activePage === 'settings') {
      return <SettingsPage />;
    }
    if (activePage === 'knowledge') {
      return <KnowledgePage />;
    }
    if (activePage === 'learning') {
      return <LearningPage />;
    }
    if (activePage === 'testbench') {
      return <TestBenchPage />;
    }
    if (activePage === 'promptbench') {
      return <PromptBenchPage />;
    }
    if (activePage === 'bakeoff') {
      return <BakeoffPage />;
    }
    if (activePage === 'ratings') {
      return <RatingsPage />;
    }
    if (activePage === 'copypools') {
      return <CopyPoolsPage />;
    }
    if (activePage === 'wordbank') {
      return <WordBankPage />;
    }
    if (activePage === 'schedule') {
      // Calendar planner (Chunk W7). The Word Binder's old Cal view lives here now.
      return <SchedulePage />;
    }
    if (activePage === 'reports') {
      return <ReportsPage />;
    }
    if (activePage === 'socialpanel') {
      // Social panel (2026-10-29): lesson posts, its own page. Not the old 'social' workspace below.
      return <SocialPanelPage />;
    }

    // The old stage screens and the Related Words workspace are gone
    // (2026-10-22). The Social tab is hidden until the daily set generator is
    // built: its screens (SocialWorkspace, SocialReviewQueue, SocialBinder) are
    // kept in the repo, unmounted, so a stale 'social' workspace opens Content.
    switch (activeWorkspace) {
      case 'game':
      case 'social':
        return <ContentPage />;
      case 'ads':
        return <ComingSoon title="Sponsors" />;
      default:
        return <GameWorkspace />;
    }
  };

  return (
    <div className="app-layout">
      <TopNav />
      <div className="app-body">
        <WordBinder />
        <div className={`app-content-area ${splitChat ? 'split-chat-active' : ''}`}>
          <main className="app-main">
            {renderMain()}
          </main>
          {splitChat && <ChatDrawer mode="side" />}
        </div>
      </div>
      {chatOpen && !splitChat && <ChatDrawer mode="bottom" />}
    </div>
  );
}
